import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../hooks/useRedux';
import { deleteDraftInvoice } from '../features/draft-invoice/draft-invoice.slice';
import { getDraftInvoiceDetails } from '../features/invoice/invoice.slice';
import {
    TableRow,
    TableCell,
    TableHeader,
    TableColumn,
    MobileCardRow,
    MobileCardWrapper,
    TableBody,
} from '../components/dashboard/table-components';
import './../scss/components/invoice-table-detailed.style.scss';
import { useDraftInvoice } from '../hooks/useDraftInvoice';
import { SearchIcon, ArrowLeftIcon, ArrowRightIcon, SendIcon, EditIcon, DeleteIcon, CheckIcon, EditIconBtn, DownloadIcon } from './../assets/dashboard-icon.svg';
import { formatAmount, getStageBadgeClass, getStatusBadgeClass, handleDownloadDraftInvoice, mapInvoiceStatus } from '../helper/common.helper';
import { calculatePaginationPages } from '../helper/pagination.helper';
import { NoInvoicesYet } from './dashboard/no-invoices-yet.component';

interface InvoiceTableDetailedProps {
    className?: string;
}

export const DraftInvoiceSection: React.FC<InvoiceTableDetailedProps> = ({
    className = ''
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const lang = new URLSearchParams(window.location.search).get('lang') || 'en';

    const {
        fetchDraftInvoices,
        invoicesData,
        loading,
        error,
        pagination,
        searchQuery,
        setCurrentPage,
        goToNextPage,
        goToPreviousPage,
        handleSearch
    } = useDraftInvoice();

    const dispatch = useAppDispatch();

    const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState<boolean>(false);
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<number | null>(null);
    const [editingInvoiceNumber, setEditingInvoiceNumber] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const currentYear = new Date().getFullYear();
    const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        handleSearch(query);

        if (!query.trim()) {
            setIsSearchDropdownOpen(false);
            return;
        }

        try {
            setIsSearchDropdownOpen(true);
        } catch (error: any) {
            console.error('Search error:', error);
            setIsSearchDropdownOpen(false);
        }
    };
    useEffect(() => {
        fetchDraftInvoices();
    }, [fetchDraftInvoices]);



    const handleEditButton = async (invoiceNumber: string) => {
        setEditingInvoiceNumber(invoiceNumber);
        try {
            navigate(`/dashboard/edit-invoice/${invoiceNumber}`);
        } catch (error: any) {
            console.error('Navigation error:', error);
            const errMsg = t('NAVIGATION_FAILED') || 'Failed to navigate to edit invoice';
            toast.error(typeof errMsg === 'string' ? errMsg : 'Failed to navigate to edit invoice');
        } finally {
            setEditingInvoiceNumber(null);
        }
    };
    const handleDraftInvoice = async (id: string) => {
        try {
            // Navigate with correct URL format first
            navigate(`/v2/create-invoice?lang=${lang}&preId=${id}`);

            // Then call API to get draft invoice details
            await dispatch(getDraftInvoiceDetails({ invoiceId: id, lang })).unwrap();
        } catch (error: any) {
            console.error('Failed to fetch draft invoice details:', error);
            const errMsg = t('FAILED_TO_LOAD_DRAFT') || 'Failed to load draft invoice';
            toast.error(typeof errMsg === 'string' ? errMsg : 'Failed to load draft invoice');
        }
    }

    const handleDeleteDraft = async (id: number) => {
        try {
            await dispatch(deleteDraftInvoice(id)).unwrap();
            toast.success(String(t('DELETE_SUCCESS')));
            // refresh list
            fetchDraftInvoices();
        } catch (error: any) {
            const msg = typeof error === 'string' ? error : (error?.message || t('DELETE_FAILED') || 'Failed to delete draft');
            toast.error(String(msg));
        }
    }

    if (loading) {
        return (
            <div className={`invoice-table-container ${className}`}>
                <div className="invoice-table-header">
                    <h3 className="invoice-table-title">{t('MY_INVOICES')}</h3>
                </div>
                <div className="invoice-table-content">
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        {t('LOADING_INVOICES')}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`invoice-table-container ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">{t('DRAFT_INVOICES')}</h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('HEADER_SEARCH') || 'Search'}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />
                    </div>
                </div>
            </div>

            <div className="invoice-table-content">

                {/* Table */}
                {!loading && !error && invoicesData && (Array.isArray(invoicesData) && invoicesData.length >= 0) && (
                    <div className="invoice-table">
                        {!isMobile && (
                            <TableHeader>
                                {[t('DRAFT_INVOICE_NO'), t('CLIENT_NAME'), t('AMOUNT'), t('STAGE_LABEL'), t('ACTION_LABEL')].map((header, index) => (
                                    <TableColumn key={index}>{header}</TableColumn>
                                ))}
                            </TableHeader>
                        )}

                        <TableBody>
                            {loading && (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                    {t('LOADING_INVOICES')}
                                </div>
                            )}
                            {invoicesData.length === 0 && (
                                <div className="invoice-table-content">
                                    <div className="invoice-table-scroll-wrapper">
                                        <div className="invoice-table">
                                            {!loading && !error && (!invoicesData || (Array.isArray(invoicesData) && invoicesData.length === 0)) && (
                                                <div className='d-flex justify-content-center flex-column align-items-center pt-5'>
                                                    <NoInvoicesYet />
                                                    <span className='pt-3'>{t('NO_INVOICES_YET')}.</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {isMobile ? (
                                // Mobile: Card-based layout
                                (Array.isArray(invoicesData) ? invoicesData : [])?.map((invoice: any) => (
                                    <MobileCardWrapper key={invoice.id}>
                                        <MobileCardRow label={t('INVOICE_NO') }>
                                            <span className="invoice-table-detailed__invoice-link">{invoice.id}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CLIENT_NAME')}>
                                            {invoice?.customer?.name}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('AMOUNT')}>
                                            {formatAmount(invoice.amount)}

                                        </MobileCardRow>
                                        <MobileCardRow label={t('STAGE_LABEL')}>
                                            <span className={getStageBadgeClass(invoice.invoiceStatus)}>
                                                {t('DRAFT')}
                                                {/* {invoice.invoiceStatus === 2 && <CheckIcon />} */}
                                                {/* {t(mapInvoiceStatus(invoice.invoiceStatus)) || mapInvoiceStatus(invoice.invoiceStatus)} */}
                                            </span>
                                        </MobileCardRow>

                                        <MobileCardRow label={t('ACTION_LABEL')}>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("EDIT_INVOICE")}
                                                    onClick={() => handleDraftInvoice(invoice.id)}
                                                >
                                                    <EditIconBtn />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title="Download"
                                                    onClick={() => handleDownloadDraftInvoice(invoice.id, 'sv-se')}
                                                >
                                                    <DownloadIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t('DELETE_INVOICE')}
                                                    onClick={() => handleDeleteDraft(invoice.id)}
                                                    disabled={editingInvoiceNumber === invoice.invoiceNumber}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                (invoicesData as any[]).map((invoice: any) => (
                                    <TableRow key={invoice.id}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{invoice.id}</span>
                                        </TableCell>
                                        <TableCell>{invoice?.customer?.name ?? invoice.clientName}</TableCell>
                                        <TableCell>{formatAmount(invoice.amount)}</TableCell>
                                        <TableCell>
                                            <span className={getStageBadgeClass(invoice?.customerApproved)}>
                                                {/* {invoice.isManually
                                                    ? "N/A"
                                                    : (() => {
                                                        switch (invoice?.customerApproved) {
                                                            case null:
                                                                return t("Pending");
                                                            case true:
                                                                return t("Approved");
                                                            case false:
                                                                return t("Unapproved");
                                                            default:
                                                                return "";
                                                        }
                                                    })()
                                                } */}
                                                {t('DRAFT')}

                                            </span>

                                        </TableCell>
                                        <TableCell>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("EDIT_INVOICE")}
                                                    onClick={() => handleDraftInvoice(invoice.id)}
                                                >
                                                    <EditIconBtn />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title="Download"
                                                    onClick={() => handleDownloadDraftInvoice(invoice.id, 'sv-se')}
                                                >
                                                    <DownloadIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t('DELETE_INVOICE')}
                                                    onClick={() => handleDeleteDraft(invoice.id)}
                                                    disabled={editingInvoiceNumber === invoice.invoiceNumber}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                            <div className="invoice-table-detailed__action-group">
                                                {/* {getInvoiceActions(invoice).map((btn, i) => (
                                                    <button
                                                        key={i}
                                                        className="invoice-table-detailed__action-btn"
                                                        title={btn.title}                                                    >
                                                        {btn.icon}
                                                    </button>
                                                ))} */}
                                            </div>

                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>
                )}

                {/* Pagination */}
                {pagination.totalPages > 0 && (
                    <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={() => goToPreviousPage()}
                                disabled={pagination.currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP')}</span>
                            </button>

                            {calculatePaginationPages(pagination.currentPage, pagination.totalPages).map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === pagination.currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={() => goToNextPage()}
                                disabled={pagination.currentPage === pagination.totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


